import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Button, 
  Form, 
  Input, 
  Select,
  Switch,
  Space,
  Typography,
  Row,
  Col,
  App
} from 'antd';
import { 
  ArrowLeftOutlined, 
  SaveOutlined 
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { websiteApi, websiteGeneratorApi } from '../services/api';
import { CreateWebsiteDto, UpdateWebsiteDto } from '../types/website';
import ThemePreview from '../components/ThemePreview';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const WebsiteForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const isEdit = !!id;
  const [selectedTheme, setSelectedTheme] = React.useState<string>('modern');

  const { data: website, isLoading } = useQuery(
    ['website', id],
    () => websiteApi.getById(id!),
    { enabled: isEdit }
  );


  const { data: themes = [], isLoading: themesLoading } = useQuery('themes', () => websiteGeneratorApi.getThemes(), {
    onSuccess: (data) => {
      console.log('Themes loaded:', data);
    },
    onError: (error) => {
      console.error('Error loading themes:', error);
    }
  });

  const createMutation = useMutation(websiteApi.create, {
    onSuccess: async (data) => {
      message.success('Website created successfully!');
      queryClient.invalidateQueries('websites');
      navigate('/websites');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create website');
    },
  });

  const updateMutation = useMutation(
    (data: UpdateWebsiteDto) => websiteApi.update(id!, data),
    {
      onSuccess: async () => {
        message.success('Website updated successfully!');
        queryClient.invalidateQueries('websites');
        queryClient.invalidateQueries(['website', id]);
        navigate('/websites');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Failed to update website');
      },
    }
  );

  useEffect(() => {
    if (isEdit && website) {
      console.log('Setting form values for website:', website);
      console.log('Website videos:', website.videos);
      
      // Transform videos to IDs if they are objects
      
      const themeValue = website.theme || 'modern';
      setSelectedTheme(themeValue);
      
      form.setFieldsValue({
        name: website.name,
        description: website.description,
        domain: website.domain,
        url: website.url,
        type: website.type,
        outputDir: website.outputDir,
        isActive: website.isActive,
        hasDirLinks: website.hasDirLinks || false,
        theme: themeValue,
      });

    }
  }, [isEdit, website, form]);

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      if (isEdit) {
        updateMutation.mutate(values);
      } else {
        createMutation.mutate(values as CreateWebsiteDto);
      }
    }).catch(() => {
      message.error('Please fix the validation errors');
    });
  };

  const handleCancel = () => {
    navigate('/websites');
  };


  if (isEdit && isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={handleCancel}
          style={{ marginRight: 16 }}
        >
          Back to Websites
        </Button>
        <Title level={2} style={{ display: 'inline' }}>
          {isEdit ? `Edit Website: ${website?.name}` : 'Create New Website'}
        </Title>
      </div>

      <Row justify="center">
        <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={20}>
          <Card 
            style={{ 
              margin: '0 16px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
            }}
            styles={{
              body: { 
                padding: '32px' 
              }
            }}
          >
            <Form 
              form={form} 
              layout="vertical" 
              onFinish={handleSubmit}
              initialValues={{
                isActive: true,
                hasDirLinks: false,
                type: 'static',
                outputDir: 'dist'
              }}
            >
              {/* Website Information Section */}
              <div style={{ marginBottom: '32px' }}>
                <Title level={4} style={{ 
                  marginBottom: '20px',
                  color: '#1890ff',
                  borderBottom: '2px solid #f0f0f0',
                  paddingBottom: '8px'
                }}>
                  Website Information
                </Title>
                
                <Row gutter={[16, 0]}>
                  <Col xs={24} lg={12}>
                    <Form.Item
                      name="name"
                      label="Website Name"
                      rules={[
                        { required: true, message: 'Please enter website name' },
                        { min: 2, message: 'Name must be at least 2 characters' },
                        { max: 100, message: 'Name must be less than 100 characters' }
                      ]}
                    >
                      <Input placeholder="My Portfolio Website" />
                    </Form.Item>
                  </Col>
                  
                  <Col xs={24} lg={12}>
                    <Form.Item
                      name="domain"
                      label="Domain"
                      rules={[
                        { required: true, message: 'Please enter domain' },
                        { 
                          pattern: /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.?[a-zA-Z]{2,}$/, 
                          message: 'Please enter a valid domain (e.g., example.com)' 
                        }
                      ]}
                    >
                      <Input 
                        placeholder="example.com"
                        onChange={(e) => {
                          const domain = e.target.value;
                          if (domain && !isEdit) {
                            // Auto-fill URL field with https:// + domain
                            form.setFieldsValue({ url: `https://${domain}` });
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 0]}>
                  <Col xs={24} lg={12}>
                    <Form.Item
                      name="url"
                      label="URL"
                      rules={[
                        { required: true, message: 'Please enter URL' },
                        { type: 'url', message: 'Please enter a valid URL' }
                      ]}
                    >
                      <Input placeholder="https://example.com" />
                    </Form.Item>
                  </Col>
                  
                  <Col xs={24} lg={12}>
                    <Form.Item
                      name="type"
                      label="Website Type"
                      rules={[{ required: true, message: 'Please select website type' }]}
                    >
                      <Select placeholder="Select website type">
                        <Option value="static">Static Website</Option>
                        <Option value="spa">Single Page Application (SPA)</Option>
                        <Option value="ssr">Server-Side Rendered (SSR)</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="description"
                  label="Description"
                  rules={[
                    { max: 500, message: 'Description must be less than 500 characters' }
                  ]}
                >
                  <TextArea 
                    rows={3} 
                    placeholder="Brief description of your website"
                  />
                </Form.Item>

                <Row gutter={[16, 0]}>
                  <Col xs={24} lg={12}>
                    <Form.Item
                      name="outputDir"
                      label="Output Directory"
                    >
                      <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.domain !== currentValues.domain}>
                        {({ getFieldValue }) => {
                          const domain = getFieldValue('domain');
                          const outputDir = domain ? `/static-sites/websites/${domain}` : '/static-sites/websites/';
                          
                          return (
                            <Input 
                              value={outputDir}
                              disabled
                              placeholder="/static-sites/websites/your-domain"
                              readOnly
                            />
                          );
                        }}
                      </Form.Item>
                    </Form.Item>
                  </Col>
                  
                  <Col xs={24} lg={12}>
                    <Form.Item
                      name="isActive"
                      label="Active Status"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                  
                  <Col xs={24} lg={12}>
                    <Form.Item
                      name="hasDirLinks"
                      label="Already Dir Links?"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Theme Section */}
              <div style={{ marginBottom: '32px' }}>
                <Title level={4} style={{ 
                  marginBottom: '20px',
                  color: '#1890ff',
                  borderBottom: '2px solid #f0f0f0',
                  paddingBottom: '8px'
                }}>
                  Theme Selection
                </Title>
                
                <Form.Item
                  name="theme"
                  rules={[{ required: true, message: 'Please select a theme' }]}
                  initialValue="modern"
                >
                  <div style={{ display: 'none' }}>
                    <Select
                      placeholder={themesLoading ? "Loading themes..." : "Select a theme"}
                      loading={themesLoading}
                      notFoundContent={themesLoading ? "Loading..." : "No themes found"}
                      value={selectedTheme}
                      onChange={(value) => {
                        setSelectedTheme(value);
                        form.setFieldsValue({ theme: value });
                      }}
                    >
                      {themes.map((theme: any) => (
                        <Option key={theme.name} value={theme.name}>
                          {theme.name.charAt(0).toUpperCase() + theme.name.slice(1)} Theme
                        </Option>
                      ))}
                    </Select>
                  </div>
                </Form.Item>

                {/* Theme Preview Grid */}
                {themesLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Text>Loading themes...</Text>
                  </div>
                ) : (
                  <Row gutter={[16, 16]}>
                    {themes.map((theme: any) => (
                      <Col xs={24} sm={12} md={8} lg={6} key={theme.name}>
                        <ThemePreview
                          theme={theme}
                          isSelected={selectedTheme === theme.name}
                          onClick={() => {
                            setSelectedTheme(theme.name);
                            form.setFieldsValue({ theme: theme.name });
                          }}
                        />
                      </Col>
                    ))}
                  </Row>
                )}
              </div>




              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={createMutation.isLoading || updateMutation.isLoading}
                  >
                    {isEdit ? 'Update Website' : 'Create Website'}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default WebsiteForm;
